// src/admin/components/PurchaseOrdersManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  Pagination,
  TextField,
  InputAdornment,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { PurchaseOrder, PurchaseOrderStatus, PO_STATUS_COLORS, PAYMENT_STATUS_COLORS, PaymentStatus } from './PurchaseOrder';
import { PurchaseOrderService } from './purchaseOrderService';
import { useAuth } from '../../../contexts/AuthContext';
import POCreateDrawer from './POCreateDialog';
import POEditDrawer from './POEditDialog';
import POViewDrawer from './POViewDialog';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import StoreIcon from '@mui/icons-material/Store';
import ToastNotification from '../common/Toastnotification';
import { formatCurrency } from '../../../util/Formatcurrency';
import { usePurchaseOrdersPaginated, usePOStats, usePOMutations, PO_KEYS } from '../../../hooks/usePurchaseOrders';
import { useQueryClient } from '@tanstack/react-query';

const ORDERS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const poStatusToSoftColor: Record<string, string> = {
  success: 'success', warning: 'warning', info: 'info', error: 'error',
  primary: 'primary', secondary: 'secondary', default: 'default',
};

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();
  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box sx={{
      position: 'fixed', top: rect.top, left: rect.left,
      width: rect.width, height: rect.height,
      backgroundColor: overlayBg, backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
    }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading orders...</Typography>
      </Box>
    </Box>
  );
};

const PurchaseOrdersManagement: React.FC = () => {
  const { currentUser, hasPermission, isSuperAdmin } = useAuth();
  const queryClient = useQueryClient();
  const invalidateAll = () => queryClient.invalidateQueries({ queryKey: PO_KEYS.all });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });
  const [deleteTarget, setDeleteTarget] = useState<PurchaseOrder | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  // ── Pagination & filter state ─────────────────────────────────────────────
  // FIX: Removed all cursor state (lastVisible, firstVisible, cursors).
  // Seek-based pagination only needs the current page number.
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [paymentFilter, setPaymentFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { companyProfile } = useCompany();
  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);

  const canView = isSuperAdmin || hasPermission('purchase_order.view');
  const canCreate = isSuperAdmin || hasPermission('purchase_order.create');
  const canEdit = isSuperAdmin || hasPermission('purchase_order.edit');
  const canDelete = isSuperAdmin || hasPermission('purchase_order.delete');

  // ── React Query hooks ─────────────────────────────────────────────────────
  // FIX: No longer passes a `cursors` object — just page number + filters.
  const {
    data: ordersData,
    isLoading: ordersLoading,
    isFetching: ordersIsFetching,
    isError: ordersError,
    error: ordersErrorObj,
  } = usePurchaseOrdersPaginated({
    page: currentPage,
    search: searchQuery,
    status: statusFilter,
    payment: paymentFilter,
    pageSize: ORDERS_PER_PAGE,
  });

  const { data: statsData } = usePOStats();
  const { remove } = usePOMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const orders = ordersData?.orders ?? [];
  const totalOrders = ordersData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalOrders / ORDERS_PER_PAGE);
  const loading = ordersLoading || ordersIsFetching;

  const totalValue = statsData?.totalValue ?? 0;
  const totalPaid = statsData?.totalPaid ?? 0;
  const totalOutstanding = statsData?.totalOutstanding ?? 0;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast(t => ({ ...t, open: false }));

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  // FIX: Simplified — no cursor state to reset, just set page to 1.
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  // FIX: Simplified — just set the page number directly.
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const handlePaymentFilterChange = (value: string) => {
    setPaymentFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    setPaymentFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || paymentFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all', paymentFilter !== 'all'].filter(Boolean).length;

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeletePO = (po: PurchaseOrder) => setDeleteTarget(po);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      await remove.mutateAsync(deleteTarget.id!);
      setDeleteTarget(null);
      resetToFirstPage();
      showToast('Purchase order deleted successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to delete purchase order: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── View / Edit ───────────────────────────────────────────────────────────
  const handleViewPO = (po: PurchaseOrder) => { setSelectedPO(po); setViewDrawerOpen(true); };
  const handleEditPO = (po: PurchaseOrder) => { setSelectedPO(po); setEditDrawerOpen(true); };

  // ── Status update (called from POViewDrawer) ──────────────────────────────
  const handleStatusUpdate = async (poId: string, status: PurchaseOrderStatus) => {
    if (!currentUser) { showToast('You must be logged in to update status', 'error'); return; }
    try {
      await PurchaseOrderService.updatePOStatus(
        poId, status,
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown'
      );
      invalidateAll();
      resetToFirstPage();
      showToast('Purchase order status updated successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to update status: ${err.message || 'Unknown error'}`, 'error');
      throw err;
    }
  };

  const handleRefresh = () => {
    invalidateAll();
    resetToFirstPage();
    showToast('Purchase orders refreshed', 'info');
  };

  const getStatusIcon = (status: PurchaseOrderStatus) => {
    switch (status) {
      case PurchaseOrderStatus.APPROVED: return <CheckCircleIcon fontSize="small" />;
      case PurchaseOrderStatus.ORDERED: return <ShoppingCartIcon fontSize="small" />;
      case PurchaseOrderStatus.RECEIVED: return <LocalShippingIcon fontSize="small" />;
      case PurchaseOrderStatus.CANCELLED: return <CancelIcon fontSize="small" />;
      default: return null;
    }
  };

  if (loading && orders.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading purchase orders...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <StoreIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary">
              You don't have permission to view purchase orders. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: { md: '100%' },
      display: 'flex', flexDirection: 'column',
      overflow: { xs: 'visible', md: 'hidden' },
      p: { xs: 2, sm: 3 },
    }}>
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2} sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <StoreIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Purchase Orders
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage supplier orders and deliveries
              </Typography>
            </Box>
          </Stack>
          {canCreate && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDrawerOpen(true)}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
              Create Purchase Order
            </Button>
          )}
        </Stack>

        {/* {ordersError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {(ordersErrorObj as Error)?.message || 'Failed to fetch purchase orders'}
          </Alert>
        )} */}

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Total Orders</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                  {totalOrders}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>
                      {orders.filter(o => o.status === PurchaseOrderStatus.RECEIVED).length} received
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="warning.main" noWrap>
                      {orders.filter(o => o.status === PurchaseOrderStatus.DRAFT || o.status === PurchaseOrderStatus.PENDING).length} pending
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Total Value</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, mb: 1, fontWeight: 700 }}>
                  {formatCurrency(totalValue, currencySymbol)}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>across all orders</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Total Paid</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, mb: 1, fontWeight: 700 }}>
                  {formatCurrency(totalPaid, currencySymbol)}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalValue > 0 ? `${((totalPaid / totalValue) * 100).toFixed(1)}% of total` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="caption" noWrap>Outstanding</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, mb: 1, fontWeight: 700 }}>
                  {formatCurrency(totalOutstanding, currencySymbol)}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalValue > 0 ? `${((totalOutstanding / totalValue) * 100).toFixed(1)}% unpaid` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <TextField
              placeholder="Search by PO number, supplier... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Order Status</InputLabel>
                    <Select value={statusFilter} onChange={(e) => handleStatusFilterChange(e.target.value)} label="Order Status">
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.DRAFT}>Draft</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.PENDING}>Pending</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.APPROVED}>Approved</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.ORDERED}>Ordered</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.RECEIVED}>Received</MenuItem>
                      <MenuItem value={PurchaseOrderStatus.CANCELLED}>Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Payment Status</InputLabel>
                    <Select value={paymentFilter} onChange={(e) => handlePaymentFilterChange(e.target.value)} label="Payment Status">
                      <MenuItem value="all">All Payments</MenuItem>
                      <MenuItem value={PaymentStatus.UNPAID}>Unpaid</MenuItem>
                      <MenuItem value={PaymentStatus.PARTIAL}>Partial</MenuItem>
                      <MenuItem value={PaymentStatus.PAID}>Paid</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${orders.length} orders matching your filters`
                        : `Showing ${orders.length} orders on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
        {orders.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Orders Match Your Filters' : 'No Purchase Orders Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No purchase orders available yet. Create your first purchase order to get started.'}
                </Typography>
                {hasActiveFilters ? (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>Clear Filters</Button>
                ) : canCreate && (
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDrawerOpen(true)}>
                    Create Purchase Order
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {orders.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1, overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' }, mb: 2, position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 700, md: 1000 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>PO Number</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Supplier</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Payment</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Expected Delivery</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Created By</TableCell>
                    {(canView || canEdit || canDelete) && (
                      <TableCell align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((po) => (
                    <TableRow key={po.id} hover sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      '&:last-child td, &:last-child th': { borderBottom: 0 },
                    }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {po.poNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {po.createdAt.toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: { xs: 'block', sm: 'none' } }}>
                          {po.supplierName}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{po.supplierName}</Typography>
                        <Typography variant="caption" color="text.secondary">{po.supplierEmail}</Typography>
                      </TableCell>

                      <TableCell>
                        <Stack direction="column" spacing={0.5}>
                          <Chip
                            label={po.status} size="small" variant="outlined"
                            icon={getStatusIcon(po.status) || undefined}
                            sx={getSoftChipSx(poStatusToSoftColor[PO_STATUS_COLORS[po.status]] ?? 'default', isDark)}
                          />
                          <Chip
                            label={po.paymentStatus} size="small" variant="outlined"
                            sx={{
                              ...getSoftChipSx(poStatusToSoftColor[PAYMENT_STATUS_COLORS[po.paymentStatus]] ?? 'default', isDark),
                              display: { xs: 'flex', md: 'none' },
                            }}
                          />
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Chip
                          label={po.paymentStatus} size="small" variant="outlined"
                          sx={getSoftChipSx(poStatusToSoftColor[PAYMENT_STATUS_COLORS[po.paymentStatus]] ?? 'default', isDark)}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {formatCurrency(po.totalAmount, currencySymbol)}
                        </Typography>
                        {po.paidAmount > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Paid: {formatCurrency(po.paidAmount, currencySymbol)}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {po.expectedDeliveryDate ? (
                          <Typography variant="body2">{po.expectedDeliveryDate.toLocaleDateString()}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Not set</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="body2">{po.createdByName}</Typography>
                      </TableCell>

                      {(canView || canEdit || canDelete) && (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                            {canView && (
                              <Tooltip title="View Details">
                                <IconButton onClick={() => handleViewPO(po)} size="small" color="primary">
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canEdit && po.status === PurchaseOrderStatus.DRAFT && (
                              <Tooltip title="Edit">
                                <IconButton onClick={() => handleEditPO(po)} size="small" color="info">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeletePO(po)}
                                  disabled={deleteLoading === po.id}
                                  size="small" color="error"
                                >
                                  {deleteLoading === po.id
                                    ? <CircularProgress size={16} />
                                    : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalOrders === 0 ? 0 : (currentPage - 1) * ORDERS_PER_PAGE + 1}–
                    {Math.min(currentPage * ORDERS_PER_PAGE, totalOrders)} of {totalOrders} orders
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Drawers ── */}
      <POCreateDrawer
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        onSuccess={handleRefresh}
        onShowToast={showToast}
      />

      {selectedPO && (
        <>
          <POEditDrawer
            open={editDrawerOpen}
            onClose={() => { setEditDrawerOpen(false); setSelectedPO(null); }}
            onSuccess={handleRefresh}
            purchaseOrder={selectedPO}
            onShowToast={showToast}
          />
          <POViewDrawer
            open={viewDrawerOpen}
            onClose={() => { setViewDrawerOpen(false); setSelectedPO(null); }}
            purchaseOrder={selectedPO}
            onStatusUpdate={handleStatusUpdate}
            onRefresh={handleRefresh}
          />
        </>
      )}

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Purchase Order</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete PO{' '}
            <strong>"{deleteTarget?.poNumber}"</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button
            variant="contained" color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default PurchaseOrdersManagement;